import { createElement } from 'react';
import { createTheme } from '@mui/material';
import type { PaletteMode } from '@mui/material';
import TeenProgress from '@/pages/teen/TeenProgress';
import TeenHome from '@/pages/teen/TeenHome';
import TeenWeek from '@/pages/teen/TeenWeek';
import { appTHeme, buildChildTheme, buildTeenTheme, getPalette } from '@/utils/theme';
import {
  adultNavigationItems,
  childNavigationItems,
  supervisorNavigationItems,
  teenNavigationItems,
} from '@/components/layout/NavigationItems';
import type { PersonaProfile } from './types';

// Persona-registret. Profiler komponeras via {...base, ...override} (samma
// mönster som childTheme/ChildActivityFormsDefs redan använder). Iteration 1:
// teen ärver barn-vyerna (views={}) under teen-tema/-nav/-i18n med companion AV.
//
// INVARIANT (barn/vuxen oförändrade): child- och adult-profilernas buildTheme
// wrappar den BEFINTLIGA getPalette + appTHeme/childTheme så att resultatet blir
// byte-identiskt med App.tsx:s tidigare createTheme-anrop. Adult stöder inte
// mörkt läge här — temat väljs av darkMode utanför profilen (getPalette tar mode
// som argument); adultProfile.theme.buildTheme får mode och speglar det. Child
// ignorerar mode (childPalette är alltid ljus), precis som getPalette(_, true).

// Adult: identiskt med tidigare `createTheme({ ...appTHeme, palette: getPalette(mode) })`.
const buildAdultTheme = (mode: PaletteMode) =>
  createTheme({ ...appTHeme, palette: getPalette(mode, false) });

export const adultProfile: PersonaProfile = {
  persona: 'adult',
  i18nNamespace: '',
  theme: { supportsDarkMode: true, buildTheme: buildAdultTheme },
  navigation: adultNavigationItems,
  views: {},
  features: {
    companion: { enabled: false, defaultMode: 'off', modes: ['off'] },
    stars: { enabled: false, displayAs: 'none' },
    ai: { enabled: true },
    gps: { enabled: true },
  },
};

// Handledarprofilen. Delningen av handledarläget och brukarläget (Blommans
// "dela handledareläget och brukarläget") är ett LAGER OVANPÅ rollen — inte en ny
// sanning bredvid SupervisorContext. isSupervising och currentClient förblir ENDA
// sanningen för gaterna och effectiveChildMode; supervisorProfile byter bara
// presentationen (navigation/identitet) när rollvalet är 'supervisor'.
//
// Komponerad ur adultProfile ({...base, ...override}, samma mönster som teen) och
// override:ar ENBART navigation: handledaren och den vuxna brukaren delade förut
// exakt samma navbar. Tema, features och i18nNamespace ärvs oförändrade från
// vuxenprofilen — persona-axeln (child/teen/adult) rörs INTE, rollen är ortogonal
// mot den, så profile.persona är fortfarande 'adult' i handledarläge och allt som
// gatar på persona (useStarValuePersona, App.tsx:s typsnittsval, PersonaView)
// beter sig byte-identiskt.
export const supervisorProfile: PersonaProfile = {
  ...adultProfile,
  navigation: supervisorNavigationItems,
};

export const childProfile: PersonaProfile = {
  persona: 'child',
  i18nNamespace: 'child',
  theme: { supportsDarkMode: true, buildTheme: buildChildTheme },
  navigation: childNavigationItems,
  views: {},
  features: {
    companion: { enabled: true, defaultMode: 'companion', modes: ['companion'] },
    stars: { enabled: true, displayAs: 'stars' },
    ai: { enabled: false },
    gps: { enabled: true },
  },
};

// Teen = barn-profilen med teen-specifika override:ar. Companion default AV i
// iteration 1, mörkt läge stöds, stjärnor visas som framstegs-indikator.
const teenOverride: Partial<PersonaProfile> = {
  persona: 'teen',
  i18nNamespace: 'teen',
  theme: { supportsDarkMode: true, buildTheme: buildTeenTheme },
  navigation: teenNavigationItems,
  features: {
    companion: { enabled: true, defaultMode: 'off', modes: ['off', 'coach', 'companion'] },
    stars: { enabled: true, displayAs: 'progress' },
    ai: { enabled: false },
    gps: { enabled: true },
  },
  views: {
    '/': () => createElement(TeenHome),
    '/week': () => createElement(TeenWeek),
    '/progress': () => createElement(TeenProgress),
  },
};

export const teenProfile: PersonaProfile = { ...childProfile, ...teenOverride };
